/**
 * 팩 ↔ 블럭 교차 계산 헬퍼 — 대시보드의 여러 컴포넌트가 공유하는 순수 함수.
 *
 * 4층 구조(Block → Pack → Preset → Slice) 중 "팩이 조합한 블럭 id 목록" 을
 * 역참조하거나 집계할 때 사용한다. `OntologyBlock` / `OntologyPack` 타입은
 * `src/dashboard/src/api/admin-client.ts` 에서 정의된 것과 동일 shape.
 *
 * 설계 원칙:
 * - 전부 순수 함수 — React 상태에 의존하지 않음. 단위 테스트 용이.
 * - 입력은 `ReadonlyArray` · 결과도 새 배열/맵 반환 (입력 변형 금지).
 * - 서버 카탈로그가 늦게 로드되거나 누락됐을 때도 안전하게 빈 값 반환.
 */
import type { BlockCategory, OntologyBlock, OntologyPack } from "../../api/admin-client.ts"

/**
 * 특정 블럭 id 를 참조하는 팩들을 찾는다 — 블럭 카드의 "이 블럭을 쓰는 팩" 칩 리스트용.
 *
 * @param packs 팩 카탈로그 전체
 * @param blockId 역참조할 블럭 id
 * @returns 해당 블럭을 `blockIds` 에 포함한 팩들 (입력 순서 유지). 없으면 빈 배열.
 */
export function packsUsingBlock(
	packs: ReadonlyArray<OntologyPack>,
	blockId: string
): ReadonlyArray<OntologyPack> {
	return packs.filter((pack) => pack.blockIds.includes(blockId))
}

/**
 * 여러 팩이 조합하는 블럭 id 의 합집합 — 서비스 카드/Overview 의 "Built from N blocks" 라벨용.
 *
 * `blockIds` 는 각 팩이 이미 순서대로 기록한 배열. 팩 간 중복은 dedupe 하되 **최초 등장 순서** 를
 * 유지해 UI 칩 리스트를 안정적으로 렌더할 수 있게 한다.
 *
 * @param packs 선택된 팩들 (주로 서비스의 `ontologySlice.packIds` 로 필터링된 서브셋)
 * @returns 고유 블럭 id 배열 (최초 등장 순서)
 */
export function uniqueBlockIds(packs: ReadonlyArray<OntologyPack>): ReadonlyArray<string> {
	const seen = new Set<string>()
	const out: string[] = []
	for (const pack of packs) {
		for (const id of pack.blockIds) {
			if (!seen.has(id)) {
				seen.add(id)
				out.push(id)
			}
		}
	}
	return out
}

/**
 * 블럭 카탈로그를 카테고리별로 그룹핑 — BlocksPage 의 카테고리 탭/섹션 렌더용.
 *
 * 카탈로그 순서는 카테고리(actor → content → event → product → place → structure → decision) 로
 * 이미 정렬되어 오므로, 그룹 내부 순서 역시 카탈로그 순서를 그대로 유지한다.
 *
 * @param blocks 블럭 카탈로그 (보통 `client.getOntologyBlocks()` 응답)
 * @returns 각 카테고리 → 블럭 배열 맵. 블럭이 없는 카테고리도 빈 배열로 키를 유지.
 */
export function blocksByCategory(
	blocks: ReadonlyArray<OntologyBlock>
): Record<BlockCategory, ReadonlyArray<OntologyBlock>> {
	const out: Record<BlockCategory, OntologyBlock[]> = {
		actor: [],
		content: [],
		event: [],
		product: [],
		place: [],
		structure: [],
		decision: []
	}
	for (const block of blocks) {
		out[block.category].push(block)
	}
	return out
}
